import { useSchoolYear } from '@pelckmans/business-components/context/SchoolYearContext';
import { createContext, useContext, useEffect, useState, type PropsWithChildren } from 'react';
import type { CurrentGroup, Member } from '../../api/groups';
import { fetchCurrentGroups, fetchMembers } from '../../api/groups';
import useThrowAsyncError from '../../hooks/useThrowAsyncError';
import { isSchoolAudience } from './sharedMaterialApi';
import type { AudienceAssignment, GroupAudience, MemberAudience, UserSharedMaterial } from './sharedMaterialApi';

type SchoolRecord = Record<string, (CurrentGroup & { dateError?: string })[]>;

type GroupRecord = Record<string, (Member & { dateError?: string })[]>;

interface handleUpdateAudienceProps {
  groupId?: string;
  memberId?: string;
  dateError?: boolean;
  newAssignment?: Partial<AudienceAssignment>;
}

interface ShareModalContextType {
  groupsBySchool: SchoolRecord;
  membersByGroup: GroupRecord;
  isLoading: boolean;
  selectedGroups: GroupAudience[];
  selectedMembers: MemberAudience[];
  handleGroupToggle: (group: CurrentGroup) => void;
  handleMemberToggle: (group: CurrentGroup, memberId: string) => void;
  handleAssignmentToggle: (groupId?: string, memberId?: string) => void;
  handleUpdateAudience: ({ groupId, memberId, dateError, newAssignment }: handleUpdateAudienceProps) => void;
  resetAndMapInitialAudiences: (audiences: UserSharedMaterial['audiences']) => void;
}

const ShareModalContext = createContext<ShareModalContextType>({
  groupsBySchool: {},
  membersByGroup: {},
  isLoading: false,
  selectedGroups: [],
  selectedMembers: [],
  handleGroupToggle: () => {},
  handleMemberToggle: () => {},
  handleAssignmentToggle: () => {},
  handleUpdateAudience: () => {},
  resetAndMapInitialAudiences: () => {},
});

// Prefill a new audience with a sibling's settings, but never its id: each audience must own its own assignment
const copyAssignmentSettings = (assignment?: AudienceAssignment): AudienceAssignment | undefined => {
  if (!assignment) return undefined;

  const { id, ...settings } = assignment;

  return settings;
};

export function ShareModalContextProvider({ children }: PropsWithChildren) {
  const { currentSchoolYear } = useSchoolYear();
  const handleError = useThrowAsyncError();

  const [groupsBySchool, setGroupsBySchool] = useState<SchoolRecord>({});
  const [membersByGroup, setMembersByGroup] = useState<GroupRecord>({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [selectedGroups, setSelectedGroups] = useState<GroupAudience[]>([]);
  const [selectedMembers, setSelectedMembers] = useState<MemberAudience[]>([]);

  const DEFAULT_ASSIGNMENT: AudienceAssignment = {
    repeatable: false,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };

  const handleGroupToggle = (group: CurrentGroup) => {
    const getGroupAudience = (): GroupAudience => {
      const siblingAssignment = copyAssignmentSettings(selectedGroups[0]?.assignment);

      return {
        schoolId: group.school.id,
        groupId: group.id,
        audienceType: 'group',
        assignment: siblingAssignment,
      };
    };

    setSelectedGroups(current =>
      current.some(a => a.groupId === group.id)
        ? current.filter(a => a.groupId !== group.id)
        : [...current, getGroupAudience()],
    );

    // Remove all audienceMembers if parent group gets selected
    if (selectedMembers.some(a => a.groupId === group.id)) {
      setSelectedMembers(current => current.filter(a => a.groupId !== group.id));
    }
  };

  const handleMemberToggle = (group: CurrentGroup, memberId: string) => {
    const getMemberAudience = (): MemberAudience => {
      const siblingAssignment = copyAssignmentSettings(selectedMembers.find(a => a.groupId === group.id)?.assignment);

      return {
        schoolId: group.school.id,
        groupId: group.id,
        userId: memberId,
        audienceType: 'member',
        assignment: siblingAssignment,
      };
    };

    setSelectedMembers(current =>
      current.some(a => a.userId === memberId && a.groupId === group.id)
        ? current.filter(a => a.userId !== memberId || a.groupId !== group.id)
        : [...current, getMemberAudience()],
    );
  };

  const handleAssignmentToggle = (groupId?: string, memberId?: string) => {
    if (!groupId) return;

    if (memberId) {
      setSelectedMembers(audiences =>
        audiences.map(audience =>
          audience.userId === memberId && audience.groupId === groupId
            ? {
                ...audience,
                assignment: audience.assignment ? undefined : DEFAULT_ASSIGNMENT,
              }
            : audience,
        ),
      );
    } else {
      setSelectedGroups(audiences =>
        audiences.map(audience =>
          audience.groupId === groupId
            ? {
                ...audience,
                assignment: audience.assignment ? undefined : DEFAULT_ASSIGNMENT,
              }
            : audience,
        ),
      );
    }
  };

  const handleUpdateAudience = ({ groupId, memberId, dateError, newAssignment }: handleUpdateAudienceProps) => {
    if (!groupId || !(newAssignment || dateError)) return;

    if (memberId) {
      setSelectedMembers(audiences =>
        audiences.map(audience =>
          audience.userId === memberId && audience.groupId === groupId && audience.assignment
            ? {
                ...audience,
                dateError,
                assignment: {
                  ...audience.assignment,
                  ...newAssignment,
                },
              }
            : audience,
        ),
      );
    } else {
      setSelectedGroups(audiences =>
        audiences.map(audience =>
          audience.groupId === groupId && audience.assignment
            ? {
                ...audience,
                dateError,
                assignment: {
                  ...audience.assignment,
                  ...newAssignment,
                },
              }
            : audience,
        ),
      );
    }
  };

  const resetAndMapInitialAudiences = (audiences: UserSharedMaterial['audiences']) => {
    const groups: GroupAudience[] = [];
    const members: MemberAudience[] = [];

    audiences.forEach(audience => {
      if (audience.audienceType === 'group') groups.push(audience);
      else if (audience.audienceType === 'member') members.push(audience);
    });

    // Studio can't share on school level, so show a school share as all groups of that school
    audiences.forEach(audience => {
      if (!isSchoolAudience(audience)) return;

      // Without the id the backend starts a new assignment per group
      const assignment = audience.assignment && { ...audience.assignment, id: undefined };
      Object.values(groupsBySchool)
        .flat()
        .filter(group => group.school.id === audience.schoolId && !groups.some(a => a.groupId === group.id))
        .forEach(group =>
          groups.push({
            schoolId: group.school.id,
            groupId: group.id,
            audienceType: 'group',
            assignment,
          }),
        );
    });

    setSelectedGroups(groups);
    // A checked group already covers its members
    setSelectedMembers(members.filter(member => !groups.some(group => group.groupId === member.groupId)));
  };

  useEffect(() => {
    const getGroupsOfSchoolYear = async () => {
      setIsLoading(true);

      const fetchedGroups = await fetchCurrentGroups();

      if (fetchedGroups.length > 0) {
        const groupsBySchool: SchoolRecord = {};
        for (const group of fetchedGroups) {
          const schoolName = group.school.name;
          if (!groupsBySchool[schoolName]) groupsBySchool[schoolName] = [];

          groupsBySchool[schoolName].push(group);
        }

        const schoolYearString = `${currentSchoolYear.from.getFullYear()}-${currentSchoolYear.to.getFullYear()}`;

        const membersByGroup: GroupRecord = {};
        await Promise.all(
          fetchedGroups.map(async group => {
            const members = await fetchMembers(schoolYearString, group.id);

            membersByGroup[group.id] = members.filter(member => member.status === 'accepted');
          }),
        );

        setGroupsBySchool(groupsBySchool);
        setMembersByGroup(membersByGroup);
      }
      setIsLoading(false);
    };

    getGroupsOfSchoolYear().catch(handleError);
  }, [handleError, currentSchoolYear]);

  return (
    <ShareModalContext.Provider
      value={{
        groupsBySchool,
        membersByGroup,
        isLoading,
        selectedGroups,
        selectedMembers,
        handleGroupToggle,
        handleMemberToggle,
        handleAssignmentToggle,
        handleUpdateAudience,
        resetAndMapInitialAudiences,
      }}
    >
      {children}
    </ShareModalContext.Provider>
  );
}

export function useShareModalContext() {
  const ctx = useContext(ShareModalContext);
  if (!ctx) throw new Error('useShareModalContext must be used within a ShareModalContextProvider');

  return ctx;
}
